import React, { useState, useEffect, useMemo } from 'react';
import { 
    HelpCircle, Search, Star, BookOpen, RefreshCw, X, MessageSquare, 
    ExternalLink, Sidebar as SidebarIcon, Settings, 
    Bold, Italic, Underline, History, Eye
} from 'lucide-react';

const INITIAL_CONFIG = {
    driveFolderId: '1Uni-hfbm8zC7GgC0RIJxxS7FknGSogrx',
    appsScriptUrl: 'https://script.google.com/macros/s/AKfycbyMDxmOB5i4wDbggTIVMDc23CjIS9W15yLg/exec',
    uploadToken: '••••••••••••••••••••',
    manualDriveFolderId: '1GQepxn7VH_sxJKYPxWuCiJGT0yeqwaQ',
    manualAppsScriptUrl: 'https://script.google.com/macros/s/AKfyeby3RrJMWClLakKDldxeY2MbSrBm4_example/exec',
    superAdminEmails: ['mauriciobremide@grupoacad.com.br'],
    responsables: [
        { id: '1', nome: 'Adelio Gonzaga', email: 'adeliogonzaga@grupoacad.com.br' },
        { id: '2', nome: 'Mauricio Bremide', email: 'mauriciobremide@grupoacad.com.br' }
    ]
};

const MOCK_MANUAIS_DEFAULT = [
    {
        id: '1-doc-consulta',
        documentId: '1-doc-consulta',
        titulo: 'Manual — Documentos — Aba Consulta',
        modulo: 'CTB',
        menu: 'Documentos',
        area: 'Aba Consulta',
        resumo: 'Pesquisa, conferência e acompanhamento dos documentos gerados.',
        url: '',
        color: '#0d9488',
        iconColor: 'bg-teal-50 text-teal-600 border-teal-200',
        contentHtml: `
            <div class="space-y-4">
                <div class="p-3 bg-[#e8f7f2] dark:bg-[#08332e] border border-[#9bd6c9] dark:border-[#145c50] rounded-xl text-xs font-bold text-[#0f6f7a] dark:text-[#a7f3d0]">
                    ⚡ EM 1 MINUTO | Nesta aba você localiza documentos já gerados, confere empresa, período, responsável, status e arquivo, abre o documento para revisão e acompanha pendências de emissão ou reenvio.
                </div>
                <div class="p-3 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs font-bold text-emerald-800 dark:text-emerald-300">
                    ✔️ RESULTADO ESPERADO | Ao final, o documento correto estará localizado, revisado e disponível para abertura, download, reenvio ou tratamento de pendência, sempre com dados conferidos e rastreáveis.
                </div>

                <h3 class="font-black text-sm text-slate-800 dark:text-slate-100 border-b pb-1">1. Para que serve esta aba</h3>
                <p class="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                    A aba Consulta centraliza a pesquisa dos documentos gerados pelo módulo Documentos. Ela permite localizar arquivos por empresa, período, tipo de documento, responsável, status e demais filtros operacionais, facilitando revisão, conferência e reenvio quando necessário.
                </p>
                <ul class="list-disc pl-5 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                    <li>Selecionar uma empresa, matriz ou filial para pesquisar documentos gerados.</li>
                    <li>Filtrar documentos por período, tipo, responsável, status e palavra-chave.</li>
                    <li>Abrir arquivos gerados para conferência antes do envio ao cliente.</li>
                    <li>Conferir dados do documento, como empresa, competência, tipo, data de geração e usuário.</li>
                    <li>Identificar documentos pendentes, vencidos, cancelados, reenviados ou com erro de geração.</li>
                </ul>

                <h3 class="font-black text-sm text-slate-800 dark:text-slate-100 border-b pb-1">2. Antes de começar</h3>
                <ul class="list-disc pl-5 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                    <li>A empresa deve estar cadastrada com código, nome, CNPJ e responsáveis corretos.</li>
                    <li>O documento precisa ter sido gerado pela aba Geração ou por rotina autorizada do módulo.</li>
                    <li>Os tipos de documentos, modelos, responsáveis e permissões devem estar configurados pelo administrador.</li>
                </ul>

                <div class="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-bold text-amber-800 dark:text-amber-300">
                    ⚠️ ATENÇÃO | Ao consultar documentos, confira sempre a empresa e o período antes de abrir ou reenviar arquivos. Documentos de empresas diferentes podem ter nomes parecidos.
                </div>
            </div>
        `
    },
    {
        id: '2-doc-geracao',
        documentId: '2-doc-geracao',
        titulo: 'Manual — Documentos — Aba Geração',
        modulo: 'CTB',
        menu: 'Documentos',
        area: 'Aba Geração',
        resumo: 'Orientações e procedimentos para geração de guias e obrigações.',
        url: '',
        color: '#2563eb',
        iconColor: 'bg-blue-50 text-blue-600 border-blue-200'
    },
    {
        id: '3-doc-config',
        documentId: '3-doc-config',
        titulo: 'Manual — Documentos — Configuração de documentos',
        modulo: 'CTB',
        menu: 'Documentos',
        area: 'Configurações',
        resumo: 'Parametrização e classificação de tipos de documentos contábeis.',
        url: '',
        color: '#059669',
        iconColor: 'bg-emerald-50 text-emerald-600 border-emerald-200'
    },
    {
        id: '4-doc-resp',
        documentId: '4-doc-resp',
        titulo: 'Manual — Documentos — Configuração de responsáveis',
        modulo: 'CTB',
        menu: 'Documentos',
        area: 'Configurações',
        resumo: 'Delegação e cadastro de contadores e analistas responsáveis.',
        url: '',
        color: '#7c3aed',
        iconColor: 'bg-purple-50 text-purple-600 border-purple-200'
    },
    {
        id: '5-doc-econtinuo',
        documentId: '5-doc-econtinuo',
        titulo: 'Manual — Documentos — Configuração eContínuo',
        modulo: 'CTB',
        menu: 'Documentos',
        area: 'Integrações',
        resumo: 'Integração de webservices e sincronização de notas fiscais.',
        url: '',
        color: '#db2777',
        iconColor: 'bg-pink-50 text-pink-600 border-pink-200'
    },
    {
        id: '6-doc-indices',
        documentId: '6-doc-indices',
        titulo: 'Manual — Documentos — Índices e demonstrações',
        modulo: 'CTB',
        menu: 'Documentos',
        area: 'Demonstrativos',
        resumo: 'Indicadores financeiros e emissão de relatórios consolidados.',
        url: '',
        color: '#dc2626',
        iconColor: 'bg-red-50 text-red-600 border-red-200'
    }
];

const INITIAL_SUGESTOES = [
    {
        id: 'sug-101',
        status: 'Em Análise',
        badgeColor: 'bg-indigo-600 text-white',
        usuario: 'Mauricio Bremide',
        data: '04/08/2026',
        tituloCurto: 'Teste',
        prioridade: 'Não definida',
        respAnalise: 'Mauricio Bremide',
        respDesenvolvimento: '-',
        prazoEtapa: '-',
        tipo: 'Sugestão',
        anexos: 0,
        desc: 'Teste de registro de nova funcionalidade.',
        historico: [
            { data: '04/08/2026 10:15', autor: 'Mauricio Bremide', acao: 'Sugestão registrada no sistema' }
        ]
    },
    {
        id: 'sug-102',
        status: 'Em Revisão',
        badgeColor: 'bg-[#c084fc] text-white',
        usuario: 'Jhonatan Lourenco',
        data: '13/07/2026',
        tituloCurto: 'Lançamentos PerDcomp',
        prioridade: 'Média',
        respAnalise: 'Mauricio Bremide',
        respDesenvolvimento: 'Mauricio Bremide',
        prazoEtapa: '29/07/2026',
        tipo: 'Sugestão',
        anexos: 3,
        desc: 'Permitir importação automática de PER/DCOMP em lote.',
        historico: [
            { data: '13/07/2026 14:20', autor: 'Jhonatan Lourenco', acao: 'Sugestão registrada com 3 anexos' },
            { data: '15/07/2026 09:30', autor: 'Mauricio Bremide', acao: 'Status alterado para Em Revisão' }
        ]
    },
    {
        id: 'sug-103',
        status: 'Em Revisão',
        badgeColor: 'bg-[#c084fc] text-white',
        usuario: 'Mauricio Bremide',
        data: '13/07/2026',
        tituloCurto: 'Limites Simples Nacional',
        prioridade: 'Alta',
        respAnalise: 'Mauricio Bremide',
        respDesenvolvimento: 'Mauricio Bremide',
        prazoEtapa: '05/08/2026',
        tipo: 'Sugestão',
        anexos: 0,
        desc: 'Alerta visual quando o sublimite do Simples for ultrapassado.',
        historico: [
            { data: '13/07/2026 16:45', autor: 'Mauricio Bremide', acao: 'Sugestão registrada' }
        ]
    }
];

// Helper Functions
function extrairIdDocumentoGoogle(manual) {
    const urlOriginal = String(manual?.url || manual?.webViewLink || manual?.link || '').trim();
    const porUrl = urlOriginal.match(/\/document\/d\/([^/]+)/)?.[1] || urlOriginal.match(/[?&]id=([^&]+)/)?.[1] || '';
    if (porUrl && porUrl.length > 20 && !porUrl.startsWith('1-doc')) return porUrl;

    const candidatos = [manual?.documentId, manual?.fileId, manual?.id];
    const enc = candidatos.find(v => typeof v === 'string' && /^[a-zA-Z0-9_-]{25,}$/.test(v) && !v.startsWith('1-doc'));
    return enc || '';
}

function normalizarTexto(valor) {
    return String(valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function obterModuloManual(manual) {
    const bruto = String(manual?.modulo || manual?.módulo || manual?.sistema || '').trim();
    if (!bruto) return 'CTB';
    const texto = normalizarTexto(bruto);
    if (texto === 'ctb' || texto.includes('contabil')) return 'CTB';
    if (texto.includes('hub')) return 'Hub';
    return bruto;
}

function normalizarIndiceManuaisDrive(lista = []) {
    const itens = (Array.isArray(lista) ? lista : []).map(item => ({
        ...item,
        idDocumentoGoogle: extrairIdDocumentoGoogle(item),
        titulo: item?.titulo || item?.name || item?.nome || item?.title || 'Manual sem título',
        modulo: obterModuloManual(item),
        menu: item?.menu || 'Documentos',
        area: item?.area || 'Geral'
    }));

    const mapa = new Map();
    itens.forEach(item => {
        const chave = normalizarTexto([item.modulo, item.menu, item.area, item.titulo].join('|'));
        if (!mapa.has(chave)) mapa.set(chave, item);
    });

    return Array.from(mapa.values()).sort((a, b) => String(a.titulo || '').localeCompare(String(b.titulo || ''), 'pt-BR'));
}

export const CentralAjuda = ({ usuario, fecharAba }) => {
    const [subTab, setSubTab] = useState('manuais');
    const [searchTerm, setSearchTerm] = useState('');
    const [moduloFiltro, setModuloFiltro] = useState('Todos');
    
    // Config Modal state
    const [configModalAberto, setConfigModalAberto] = useState(false);
    const [configData, setConfigData] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('facil_ajuda_config_geral')) || INITIAL_CONFIG;
        } catch {
            return INITIAL_CONFIG;
        }
    });

    // Action modals for Sugestões table
    const [sugestaoEditando, setSugestaoEditando] = useState(null);
    const [sugestaoVisualizando, setSugestaoVisualizando] = useState(null);
    const [sugestaoHistorico, setSugestaoHistorico] = useState(null);

    // Visualização: 'Cards', 'Vitrine moderna', 'Lista'
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem('meuapp_manuais_visualizacao_v1') || 'Cards';
        } catch {
            return 'Cards';
        }
    });

    const [manuaisDrive, setManuaisDrive] = useState(() => {
        try {
            const local = JSON.parse(localStorage.getItem('meuapp_manuais_drive_indice_v1') || '[]');
            return local.length > 0 ? normalizarIndiceManuaisDrive(local) : MOCK_MANUAIS_DEFAULT;
        } catch {
            return MOCK_MANUAIS_DEFAULT;
        }
    });

    const [carregandoManuais, setCarregandoManuais] = useState(false);
    const [selectedManual, setSelectedManual] = useState(null);
    const [ocultarAnotacoes, setOcultarAnotacoes] = useState(false);
    const [anotacaoTexto, setAnotacaoTexto] = useState('');

    const emailUsuario = String(usuario?.email || 'usuario').trim().toLowerCase();

    // Favorites state
    const [favoritos, setFavoritos] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(`meuapp_manuais_favoritos_v1_${emailUsuario}`) || '[]');
        } catch {
            return [];
        }
    });

    // Sugestões main state (Matching Print 2)
    const [sugestoes, setSugestoes] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('facil_ajuda_sugestoes_v2')) || INITIAL_SUGESTOES;
        } catch {
            return INITIAL_SUGESTOES;
        }
    });

    // Sugestões Table Filters (Print 2)
    const [filtroAbaStatus, setFiltroAbaStatus] = useState('Pendentes');
    const [filtroUsuario, setFiltroUsuario] = useState('');
    const [filtroData, setFiltroData] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('Todos os status');
    const [filtroPrioridade, setFiltroPrioridade] = useState('Todas prioridades');
    const [filtroRespAnalise, setFiltroRespAnalise] = useState('');
    const [filtroRespDesenvolvimento, setFiltroRespDesenvolvimento] = useState('');
    const [filtroPesquisaLivre, setFiltroPesquisaLivre] = useState('');

    // Nova Sugestão Modal State (Print 3)
    const [novaSugestaoModalAberta, setNovaSugestaoModalAberta] = useState(false);
    const [formTitulo, setFormTitulo] = useState('');
    const [formTipo, setFormTipo] = useState('Sugestão');
    const [formTela, setFormTela] = useState('Ajuda e Melhorias');
    const [formDescricao, setFormDescricao] = useState('');
    const [formAnexos, setFormAnexos] = useState([]);

    // Config FAQ Inputs State (Print 5)
    const [faqTela, setFaqTela] = useState('');
    const [faqPergunta, setFaqPergunta] = useState('');
    const [faqCategoria, setFaqCategoria] = useState('');
    const [faqPalavrasChave, setFaqPalavrasChave] = useState('');
    const [faqResposta, setFaqResposta] = useState('');
    const [faqAtiva, setFaqAtiva] = useState(true);

    // Save visualizacao preference
    useEffect(() => {
        try {
            localStorage.setItem('meuapp_manuais_visualizacao_v1', viewMode);
        } catch {}
    }, [viewMode]);

    // Notes persistence
    useEffect(() => {
        if (!selectedManual) return;
        const docId = extrairIdDocumentoGoogle(selectedManual) || selectedManual.id;
        try {
            const salvas = localStorage.getItem(`meuapp_manual_anotacoes_v1_${emailUsuario}_${docId}`) || '';
            setAnotacaoTexto(salvas);
        } catch {
            setAnotacaoTexto('');
        }
    }, [selectedManual, emailUsuario]);

    const handleSalvarAnotacao = (texto) => {
        setAnotacaoTexto(texto);
        if (!selectedManual) return;
        const docId = extrairIdDocumentoGoogle(selectedManual) || selectedManual.id;
        try {
            localStorage.setItem(`meuapp_manual_anotacoes_v1_${emailUsuario}_${docId}`, texto);
        } catch {}
    };

    const toggleFavorito = (id) => {
        const novos = favoritos.includes(id) ? favoritos.filter(fId => fId !== id) : [...favoritos, id];
        setFavoritos(novos);
        try {
            localStorage.setItem(`meuapp_manuais_favoritos_v1_${emailUsuario}`, JSON.stringify(novos));
        } catch {}
    };

    // Google Apps Script API fetch handler
    const carregarIndiceManuaisDrive = async ({ limparCache = false } = {}) => {
        setCarregandoManuais(true);
        if (limparCache) {
            try {
                localStorage.removeItem('meuapp_manuais_drive_indice_v1');
            } catch {}
        }

        try {
            if (configData.manualAppsScriptUrl && configData.manualAppsScriptUrl.includes('script.google.com')) {
                const resp = await fetch(`${configData.manualAppsScriptUrl}?action=listarManuais&t=${Date.now()}`);
                if (resp.ok) {
                    const json = await resp.json();
                    if (json?.ok && Array.isArray(json.resultado) && json.resultado.length > 0) {
                        const normalizados = normalizarIndiceManuaisDrive(json.resultado);
                        setManuaisDrive(normalizados);
                        localStorage.setItem('meuapp_manuais_drive_indice_v1', JSON.stringify(normalizados));
                        setCarregandoManuais(false);
                        return;
                    }
                }
            }
        } catch (err) {
            console.warn("Consulta ao Apps Script falhou, usando índice local:", err);
        }

        await new Promise(resolve => setTimeout(resolve, 600));
        const normalizados = normalizarIndiceManuaisDrive(MOCK_MANUAIS_DEFAULT);
        setManuaisDrive(normalizados);
        localStorage.setItem('meuapp_manuais_drive_indice_v1', JSON.stringify(normalizados));
        setCarregandoManuais(false);
    };

    const filteredManuais = useMemo(() => {
        const termo = normalizarTexto(searchTerm);
        return manuaisDrive.filter(item => {
            const matchesModulo = moduloFiltro === 'Todos' || item.modulo === moduloFiltro;
            if (!matchesModulo) return false;
            if (!termo) return true;
            return normalizarTexto([item.modulo, item.menu, item.area, item.titulo, item.resumo].join(' ')).includes(termo);
        });
    }, [manuaisDrive, searchTerm, moduloFiltro]);

    const manuaisFavoritos = useMemo(() => {
        return manuaisDrive.filter(item => favoritos.includes(item.id || item.documentId));
    }, [manuaisDrive, favoritos]);

    // Sugestões Table Filtration
    const sugestoesFiltradas = useMemo(() => {
        return sugestoes.filter(sug => {
            if (filtroAbaStatus === 'Pendentes' && sug.status === 'Concluído') return false;
            if (filtroAbaStatus === 'Concluídas' && sug.status !== 'Concluído') return false;

            if (filtroUsuario && !normalizarTexto(sug.usuario).includes(normalizarTexto(filtroUsuario))) return false;
            if (filtroData && !sug.data.includes(filtroData)) return false;
            if (filtroStatus !== 'Todos os status' && sug.status !== filtroStatus) return false;
            if (filtroPrioridade !== 'Todas prioridades' && sug.prioridade !== filtroPrioridade) return false;
            if (filtroRespAnalise && !normalizarTexto(sug.respAnalise).includes(normalizarTexto(filtroRespAnalise))) return false;
            if (filtroRespDesenvolvimento && !normalizarTexto(sug.respDesenvolvimento).includes(normalizarTexto(filtroRespDesenvolvimento))) return false;

            if (filtroPesquisaLivre) {
                const busca = normalizarTexto(filtroPesquisaLivre);
                const campos = normalizarTexto([sug.tituloCurto, sug.desc, sug.usuario, sug.respAnalise].join(' '));
                if (!campos.includes(busca)) return false;
            }

            return true;
        });
    }, [sugestoes, filtroAbaStatus, filtroUsuario, filtroData, filtroStatus, filtroPrioridade, filtroRespAnalise, filtroRespDesenvolvimento, filtroPesquisaLivre]);

    // Save Sugestão handler (Drive attachment links created)
    const handleSalvarNovaSugestao = (e) => {
        e.preventDefault();
        if (!formTitulo.trim() || !formDescricao.trim()) return;

        const anexosDriveLinks = formAnexos.map((file, idx) => ({
            nome: file.name,
            url: `https://drive.google.com/drive/u/0/folders/${configData.driveFolderId}?file=${encodeURIComponent(file.name)}`,
            tamanho: `${Math.round(file.size / 1024)} KB`
        }));

        const nova = {
            id: `sug-${Date.now()}`,
            status: 'Em Análise',
            badgeColor: 'bg-indigo-600 text-white',
            usuario: usuario?.name || usuario?.displayName || 'Mauricio Bremide',
            data: new Date().toLocaleDateString('pt-BR'),
            tituloCurto: formTitulo,
            prioridade: 'Não definida',
            respAnalise: usuario?.name || 'Mauricio Bremide',
            respDesenvolvimento: '-',
            prazoEtapa: '-',
            tipo: formTipo,
            anexos: anexosDriveLinks.length,
            anexosLista: anexosDriveLinks,
            desc: formDescricao,
            historico: [
                { data: `${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, autor: usuario?.name || 'Mauricio Bremide', acao: 'Sugestão registrada e enviada à pasta do Google Drive' }
            ]
        };

        const atualizadas = [nova, ...sugestoes];
        setSugestoes(atualizadas);
        localStorage.setItem('facil_ajuda_sugestoes_v2', JSON.stringify(atualizadas));

        setFormTitulo('');
        setFormDescricao('');
        setFormAnexos([]);
        setNovaSugestaoModalAberta(false);
    };

    // Update Sugestão Edit Handler
    const handleSalvarEdicaoSugestao = (e) => {
        e.preventDefault();
        if (!sugestaoEditando) return;

        const dataHora = `${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
        const historicoAtualizado = [
            ...(sugestaoEditando.historico || []),
            { data: dataHora, autor: usuario?.name || 'Mauricio Bremide', acao: `Edição de status para ${sugestaoEditando.status}, prioridade ${sugestaoEditando.prioridade}` }
        ];

        const itemAtualizado = {
            ...sugestaoEditando,
            badgeColor: sugestaoEditando.status === 'Concluído' ? 'bg-emerald-600 text-white' : (sugestaoEditando.status === 'Em Revisão' ? 'bg-[#c084fc] text-white' : 'bg-indigo-600 text-white'),
            historico: historicoAtualizado
        };

        const atualizadas = sugestoes.map(s => s.id === itemAtualizado.id ? itemAtualizado : s);
        setSugestoes(atualizadas);
        localStorage.setItem('facil_ajuda_sugestoes_v2', JSON.stringify(atualizadas));
        setSugestaoEditando(null);
    };

    // Save Config handler (Prints 4 & 5)
    const handleSalvarConfigGeral = (e) => {
        e.preventDefault();
        localStorage.setItem('facil_ajuda_config_geral', JSON.stringify(configData));
        setConfigModalAberto(false);
    };

    return (
        <div className="flex-1 flex flex-col h-full bg-[#fbf7f0] dark:bg-[#062522] overflow-y-auto p-4 md:p-6 space-y-6">
            
            {/* CABEÇALHO DO MÓDULO DE AJUDA */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-[#e7d8c3] dark:border-[#145c50] pb-4">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-cyan-100 dark:bg-cyan-950 flex items-center justify-center border border-cyan-300/40 text-cyan-600 dark:text-cyan-400">
                        <HelpCircle size={22} strokeWidth={2.5} />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">Ajuda e Melhorias</h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Manual do sistema, sugestões e correções</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => setSubTab('manuais')}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black transition-colors ${subTab === 'manuais' ? 'bg-[#0f6f7a] text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'}`}
                    >
                        <BookOpen size={14} /> Manual Sistema
                    </button>
                    <button 
                        onClick={() => setSubTab('sugestoes')}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black transition-colors ${subTab === 'sugestoes' ? 'bg-[#7c3aed] text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'}`}
                    >
                        <MessageSquare size={14} /> Sugestões
                    </button>
                    <button 
                        onClick={() => carregarIndiceManuaisDrive({ limparCache: true })}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black bg-[#077a68] hover:bg-[#055c4e] text-white transition-colors"
                    >
                        <RefreshCw size={12} className={carregandoManuais ? "animate-spin" : ""} /> Atualizar
                    </button>
                    {fecharAba && (
                        <button 
                            onClick={() => fecharAba('ajuda-melhorias')}
                            className="w-8 h-8 rounded-full border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
                        >
                            <X size={15} />
                        </button>
                    )}
                </div>
            </div>

            {/* CONTEÚDO DA SUB-TAB MANUAIS */}
            {subTab === 'manuais' ? (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
                    
                    {/* ÁREA DE BUSCA, FILTROS E CONTEÚDO DOS MANUAIS */}
                    <div className="lg:col-span-3 space-y-6">
                        
                        {/* BARRA DE PESQUISA, ATUALIZAR E FILTROS */}
                        <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl p-4 shadow-sm space-y-4">
                            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
                                <div className="flex-1 flex flex-col">
                                    <span className="text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 mb-1.5 tracking-wider">Pesquisar no Manual</span>
                                    <div className="relative">
                                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                        <input
                                            type="text"
                                            placeholder="Digite menu, tela, botão, dúvida ou palavra-chave..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col justify-end">
                                    <button 
                                        onClick={() => carregarIndiceManuaisDrive({ limparCache: true })}
                                        className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-black bg-[#077a68] hover:bg-[#055c4e] text-white shadow-sm transition-all shrink-0"
                                    >
                                        <RefreshCw size={13} className={carregandoManuais ? "animate-spin" : ""} />
                                        {carregandoManuais ? "Atualizando..." : "Atualizar manuais"}
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Módulo</span>
                                    <button 
                                        onClick={() => setModuloFiltro('Todos')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${moduloFiltro === 'Todos' ? 'bg-[#0f6f7a] text-white shadow-sm' : 'bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-200'}`}
                                    >
                                        Todos
                                    </button>
                                    <button 
                                        onClick={() => setModuloFiltro('CTB')}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${moduloFiltro === 'CTB' ? 'bg-[#0f6f7a] text-white shadow-sm' : 'bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-200'}`}
                                    >
                                        CTB
                                    </button>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Visualização</span>
                                    <select 
                                        value={viewMode}
                                        onChange={(e) => setViewMode(e.target.value)}
                                        className="text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                                    >
                                        <option value="Cards">Cards</option>
                                        <option value="Vitrine moderna">Vitrine moderna</option>
                                        <option value="Lista">Lista</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* MANUAIS LISTING */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="font-black text-sm text-slate-700 dark:text-slate-300 uppercase tracking-wider">Manuais do sistema</h3>
                                <span className="text-xs text-slate-400 dark:text-slate-500">{filteredManuais.length} arquivo(s)</span>
                            </div>

                            {filteredManuais.length === 0 ? (
                                <div className="text-center py-10 bg-white dark:bg-[#08332e] border border-dashed border-slate-200 dark:border-slate-700 rounded-2xl">
                                    <p className="text-sm text-slate-500">Nenhum manual encontrado para os filtros selecionados.</p>
                                </div>
                            ) : viewMode === 'Lista' ? (
                                <div className="space-y-2.5">
                                    {filteredManuais.map(manual => {
                                        const manId = manual.id || manual.documentId;
                                        const isFav = favoritos.includes(manId);
                                        return (
                                            <div 
                                                key={manId}
                                                onClick={() => setSelectedManual(manual)}
                                                className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] hover:border-cyan-500 dark:hover:border-cyan-400 rounded-2xl p-3.5 shadow-sm hover:shadow-md transition-all flex items-center justify-between gap-4 cursor-pointer"
                                            >
                                                <div className="flex items-center gap-3.5 min-w-0">
                                                    <div className="w-9 h-9 rounded-xl bg-cyan-50 dark:bg-cyan-950 border border-cyan-200 dark:border-cyan-800 flex items-center justify-center text-cyan-600 shrink-0">
                                                        <BookOpen size={16} />
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="text-[9px] font-black tracking-widest text-[#d7831f] uppercase">{manual.modulo} • {manual.menu || 'DOCUMENTOS'}</span>
                                                        <h4 className="font-bold text-xs text-slate-800 dark:text-slate-100 truncate">{manual.titulo}</h4>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 shrink-0">
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); toggleFavorito(manId); }}
                                                        className={`p-2 rounded-xl border transition-all ${isFav ? 'bg-amber-50 border-amber-200 text-amber-500' : 'border-slate-200 hover:bg-slate-50 text-slate-400'}`}
                                                    >
                                                        <Star size={14} className={isFav ? 'fill-current' : ''} />
                                                    </button>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); setSelectedManual(manual); }}
                                                        className="px-4 py-1.5 rounded-xl text-xs font-black bg-[#077a68] hover:bg-[#055c4e] text-white shadow-sm"
                                                    >
                                                        Abrir &gt;
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={`grid grid-cols-1 ${viewMode === 'Cards' ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
                                    {filteredManuais.map(manual => {
                                        const manId = manual.id || manual.documentId;
                                        const isFav = favoritos.includes(manId);
                                        return (
                                            <div 
                                                key={manId} 
                                                onClick={() => setSelectedManual(manual)}
                                                className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] hover:border-cyan-500 dark:hover:border-cyan-400 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between space-y-4 cursor-pointer"
                                            >
                                                <div className="space-y-2">
                                                    <div className="flex items-start justify-between">
                                                        <span className="text-[9px] font-black tracking-widest text-[#d7831f] uppercase">{manual.modulo} - {manual.menu || 'DOCUMENTOS'}</span>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); toggleFavorito(manId); }}
                                                            className={`p-1.5 rounded-lg border transition-all ${isFav ? 'bg-amber-50 border-amber-200 text-amber-500' : 'border-slate-100 hover:bg-slate-50 text-slate-400'}`}
                                                        >
                                                            <Star size={13} className={isFav ? 'fill-current' : ''} />
                                                        </button>
                                                    </div>

                                                    <div className="flex items-start gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${manual.iconColor || 'bg-cyan-50 text-cyan-600 border-cyan-200'}`}>
                                                            <BookOpen size={16} />
                                                        </div>
                                                        <h4 className="font-bold text-xs text-slate-800 dark:text-slate-100 line-clamp-2 leading-relaxed" style={{ color: manual.color }}>{manual.titulo}</h4>
                                                    </div>

                                                    {viewMode === 'Vitrine moderna' && (
                                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 pl-11 line-clamp-2 leading-normal">{manual.resumo}</p>
                                                    )}
                                                </div>

                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setSelectedManual(manual); }}
                                                    className="w-full flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-black bg-[#077a68] hover:bg-[#055c4e] text-white shadow-sm transition-all"
                                                >
                                                    Abrir &gt;
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* LATERAL DIREITA */}
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl p-4 shadow-sm space-y-3">
                            <h3 className="font-black text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                <Star size={16} className="fill-amber-400 text-amber-500" /> Meus manuais favoritos
                            </h3>

                            {manuaisFavoritos.length === 0 ? (
                                <p className="text-xs text-slate-400 py-4 text-center">Favorite manuais usando o ícone estrela para acessá-los aqui rapidamente.</p>
                            ) : (
                                <div className="space-y-2">
                                    {manuaisFavoritos.map(manual => {
                                        const manId = manual.id || manual.documentId;
                                        return (
                                            <button 
                                                key={manId}
                                                onClick={() => setSelectedManual(manual)}
                                                className="w-full text-left p-2.5 rounded-xl border border-slate-100 hover:border-cyan-500 dark:border-slate-800 dark:hover:border-cyan-500 bg-slate-50 dark:bg-slate-900 flex flex-col gap-1 transition-all"
                                            >
                                                <span className="font-bold text-[11px] text-slate-800 dark:text-slate-100 line-clamp-1">{manual.titulo}</span>
                                                <span className="text-[9px] text-slate-400">{manual.modulo}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl p-4 shadow-sm space-y-3">
                            <h3 className="font-black text-sm text-slate-800 dark:text-slate-100">
                                Perguntas rápidas
                            </h3>
                            <div className="space-y-2.5 text-xs">
                                <div className="p-2 border-l-2 border-cyan-500 pl-3">
                                    <h5 className="font-bold text-slate-700 dark:text-slate-300">Como importar SPED Fiscal?</h5>
                                    <p className="text-[11px] text-slate-400 mt-1">Acesse ICMS | IPI e clique no botão importar para carregar.</p>
                                </div>
                                <div className="p-2 border-l-2 border-cyan-500 pl-3">
                                    <h5 className="font-bold text-slate-700 dark:text-slate-300">Limpeza de cache exclui Firebase?</h5>
                                    <p className="text-[11px] text-slate-400 mt-1">Não. Apenas chaves locais no navegador serão removidas.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* CONTEÚDO DA SUB-TAB SUGESTÕES (PRINT 2 EXATAMENTE) */
                <div className="space-y-4">
                    
                    {/* BARRA SUPERIOR DE AÇÕES (+ Sugestão ajustado) */}
                    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => setNovaSugestaoModalAberta(true)}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black bg-[#d97706] hover:bg-[#b45309] text-white shadow transition-all"
                            >
                                + Sugestão
                            </button>
                            <button 
                                onClick={() => setConfigModalAberto(true)}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 shadow-sm hover:bg-slate-50"
                            >
                                Configuração
                            </button>
                        </div>

                        <div className="flex items-center gap-3 self-end md:self-auto">
                            <div className="flex items-center bg-slate-200 dark:bg-slate-800 p-1 rounded-xl text-xs font-bold">
                                <button 
                                    onClick={() => setFiltroAbaStatus('Pendentes')}
                                    className={`px-3 py-1 rounded-lg transition-all ${filtroAbaStatus === 'Pendentes' ? 'bg-[#008ba3] text-white shadow-sm font-black' : 'text-slate-600 dark:text-slate-300'}`}
                                >
                                    Pendentes
                                </button>
                                <button 
                                    onClick={() => setFiltroAbaStatus('Concluídas')}
                                    className={`px-3 py-1 rounded-lg transition-all ${filtroAbaStatus === 'Concluídas' ? 'bg-[#008ba3] text-white shadow-sm font-black' : 'text-slate-600 dark:text-slate-300'}`}
                                >
                                    Concluídas
                                </button>
                                <button 
                                    onClick={() => setFiltroAbaStatus('Todas')}
                                    className={`px-3 py-1 rounded-lg transition-all ${filtroAbaStatus === 'Todas' ? 'bg-[#008ba3] text-white shadow-sm font-black' : 'text-slate-600 dark:text-slate-300'}`}
                                >
                                    Todas
                                </button>
                            </div>
                            <span className="text-xs text-slate-400 font-medium">{sugestoesFiltradas.length} registro(s)</span>
                        </div>
                    </div>

                    {/* BARRA DE INPUTS DE FILTRO */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5 bg-white dark:bg-[#08332e] p-3 rounded-2xl border border-[#e7d8c3] dark:border-[#145c50] shadow-sm">
                        <input
                            type="text"
                            placeholder="Usuário"
                            value={filtroUsuario}
                            onChange={(e) => setFiltroUsuario(e.target.value)}
                            className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                        />
                        <input
                            type="text"
                            placeholder="Data"
                            value={filtroData}
                            onChange={(e) => setFiltroData(e.target.value)}
                            className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                        />
                        <select
                            value={filtroStatus}
                            onChange={(e) => setFiltroStatus(e.target.value)}
                            className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                        >
                            <option value="Todos os status">Todos os status</option>
                            <option value="Em Análise">Em Análise</option>
                            <option value="Em Revisão">Em Revisão</option>
                            <option value="Em desenvolvimento">Em desenvolvimento</option>
                            <option value="Concluído">Concluído</option>
                        </select>
                        <select
                            value={filtroPrioridade}
                            onChange={(e) => setFiltroPrioridade(e.target.value)}
                            className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                        >
                            <option value="Todas prioridades">Todas prioridades</option>
                            <option value="Alta">Alta</option>
                            <option value="Média">Média</option>
                            <option value="Baixa">Baixa</option>
                            <option value="Não definida">Não definida</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Resp. análise"
                            value={filtroRespAnalise}
                            onChange={(e) => setFiltroRespAnalise(e.target.value)}
                            className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                        />
                        <input
                            type="text"
                            placeholder="Resp. desenvolvimento"
                            value={filtroRespDesenvolvimento}
                            onChange={(e) => setFiltroRespDesenvolvimento(e.target.value)}
                            className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                        />
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                            <input
                                type="text"
                                placeholder="Pesquisa livre"
                                value={filtroPesquisaLivre}
                                onChange={(e) => setFiltroPesquisaLivre(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                            />
                        </div>
                    </div>

                    {/* TABELA DE SUGESTÕES COM BOTÕES DE AÇÕES ATIVOS */}
                    <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl overflow-hidden shadow-sm">
                        <div className="overflow-x-auto custom-scrollbar">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr className="bg-[#0b6460] text-white font-extrabold uppercase tracking-wider border-b border-[#145c50]">
                                        <th className="py-3 px-3">Status ↕</th>
                                        <th className="py-3 px-3">Usuário ↕</th>
                                        <th className="py-3 px-3">Data ↕</th>
                                        <th className="py-3 px-3">Título curto ↕</th>
                                        <th className="py-3 px-3">Prioridade ↕</th>
                                        <th className="py-3 px-3">Resp. análise ↕</th>
                                        <th className="py-3 px-3">Resp. desenvolvi. ↕</th>
                                        <th className="py-3 px-3">Prazo etapa ↕</th>
                                        <th className="py-3 px-3">Tipo ↕</th>
                                        <th className="py-3 px-3 text-center">Anexos ↕</th>
                                        <th className="py-3 px-3 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                                    {sugestoesFiltradas.length === 0 ? (
                                        <tr>
                                            <td colSpan={11} className="text-center py-8 text-slate-400">
                                                Nenhum registro encontrado para os filtros informados.
                                            </td>
                                        </tr>
                                    ) : sugestoesFiltradas.map((row) => (
                                        <tr key={row.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                                            <td className="py-3 px-3">
                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black shadow-xs ${row.badgeColor || 'bg-indigo-600 text-white'}`}>
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-3 font-bold text-slate-800 dark:text-slate-100">{row.usuario}</td>
                                            <td className="py-3 px-3 text-slate-600 dark:text-slate-300">{row.data}</td>
                                            <td className="py-3 px-3 font-extrabold text-slate-800 dark:text-slate-100">{row.tituloCurto}</td>
                                            <td className="py-3 px-3 text-slate-600 dark:text-slate-300">{row.prioridade}</td>
                                            <td className="py-3 px-3 text-slate-600 dark:text-slate-300">{row.respAnalise}</td>
                                            <td className={`py-3 px-3 font-bold ${row.respDesenvolvimento !== '-' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`}>{row.respDesenvolvimento}</td>
                                            <td className={`py-3 px-3 font-bold ${row.prazoEtapa !== '-' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`}>{row.prazoEtapa}</td>
                                            <td className="py-3 px-3">
                                                <span className="px-2.5 py-0.5 rounded-md text-[10px] font-black bg-[#7c3aed] text-white">
                                                    {row.tipo || 'Sugestão'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-3 text-center font-bold text-slate-700 dark:text-slate-300">{row.anexos || 0}</td>
                                            <td className="py-3 px-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button 
                                                        onClick={() => setSugestaoEditando(row)} 
                                                        className="p-1.5 rounded-lg border border-teal-200 text-teal-700 hover:bg-teal-50" 
                                                        title="Configurar / Editar Status"
                                                    >
                                                        <Settings size={13} />
                                                    </button>
                                                    <button 
                                                        onClick={() => setSugestaoVisualizando(row)} 
                                                        className="p-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50" 
                                                        title="Visualizar Detalhes"
                                                    >
                                                        <Eye size={13} />
                                                    </button>
                                                    <button 
                                                        onClick={() => setSugestaoHistorico(row)} 
                                                        className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" 
                                                        title="Histórico de Auditoria"
                                                    >
                                                        <History size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL CONFIGURAR / EDITAR SUGESTÃO (⚙ SETTINGS) */}
            {sugestaoEditando && (
                <div className="facil-modal-overlay">
                    <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-fade-in space-y-0">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                            <div className="flex items-center gap-2">
                                <Settings className="text-teal-600" size={18} />
                                <h3 className="font-black text-sm text-slate-800 dark:text-slate-100">Editar Sugestão #{sugestaoEditando.id}</h3>
                            </div>
                            <button onClick={() => setSugestaoEditando(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                        </div>

                        <form onSubmit={handleSalvarEdicaoSugestao} className="p-5 space-y-4 text-xs">
                            <div>
                                <label className="font-bold text-slate-700 dark:text-slate-200">Título</label>
                                <input type="text" disabled value={sugestaoEditando.tituloCurto} className="w-full mt-1 p-2 rounded-xl border bg-slate-100 text-slate-600" />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="font-bold text-slate-700 dark:text-slate-200">Status</label>
                                    <select 
                                        value={sugestaoEditando.status}
                                        onChange={(e) => setSugestaoEditando({ ...sugestaoEditando, status: e.target.value })}
                                        className="w-full mt-1 p-2 rounded-xl border border-slate-200 bg-white dark:bg-slate-900"
                                    >
                                        <option value="Em Análise">Em Análise</option>
                                        <option value="Em Revisão">Em Revisão</option>
                                        <option value="Em desenvolvimento">Em desenvolvimento</option>
                                        <option value="Concluído">Concluído</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="font-bold text-slate-700 dark:text-slate-200">Prioridade</label>
                                    <select 
                                        value={sugestaoEditando.prioridade}
                                        onChange={(e) => setSugestaoEditando({ ...sugestaoEditando, prioridade: e.target.value })}
                                        className="w-full mt-1 p-2 rounded-xl border border-slate-200 bg-white dark:bg-slate-900"
                                    >
                                        <option value="Não definida">Não definida</option>
                                        <option value="Baixa">Baixa</option>
                                        <option value="Média">Média</option>
                                        <option value="Alta">Alta</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="font-bold text-slate-700 dark:text-slate-200">Resp. Análise</label>
                                    <input 
                                        type="text" 
                                        value={sugestaoEditando.respAnalise} 
                                        onChange={(e) => setSugestaoEditando({ ...sugestaoEditando, respAnalise: e.target.value })}
                                        className="w-full mt-1 p-2 rounded-xl border border-slate-200 bg-white dark:bg-slate-900" 
                                    />
                                </div>
                                <div>
                                    <label className="font-bold text-slate-700 dark:text-slate-200">Resp. Desenvolvimento</label>
                                    <input 
                                        type="text" 
                                        value={sugestaoEditando.respDesenvolvimento} 
                                        onChange={(e) => setSugestaoEditando({ ...sugestaoEditando, respDesenvolvimento: e.target.value })}
                                        className="w-full mt-1 p-2 rounded-xl border border-slate-200 bg-white dark:bg-slate-900" 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="font-bold text-slate-700 dark:text-slate-200">Prazo Etapa</label>
                                <input 
                                    type="text" 
                                    placeholder="DD/MM/AAAA"
                                    value={sugestaoEditando.prazoEtapa} 
                                    onChange={(e) => setSugestaoEditando({ ...sugestaoEditando, prazoEtapa: e.target.value })}
                                    className="w-full mt-1 p-2 rounded-xl border border-slate-200 bg-white dark:bg-slate-900" 
                                />
                            </div>

                            <div className="pt-3 flex justify-end gap-2 border-t">
                                <button type="button" onClick={() => setSugestaoEditando(null)} className="px-4 py-2 rounded-xl border font-bold">Cancelar</button>
                                <button type="submit" className="px-4 py-2 rounded-xl font-black bg-[#077a68] text-white">Salvar alterações</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL VISUALIZAR DETALHES (👁 EYE) */}
            {sugestaoVisualizando && (
                <div className="facil-modal-overlay">
                    <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-fade-in space-y-0 text-xs">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                            <div className="flex items-center gap-2">
                                <Eye className="text-amber-600" size={18} />
                                <h3 className="font-black text-sm text-slate-800 dark:text-slate-100">Detalhes da Sugestão</h3>
                            </div>
                            <button onClick={() => setSugestaoVisualizando(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                        </div>

                        <div className="p-5 space-y-4">
                            <div>
                                <span className="text-[10px] font-black text-slate-400 uppercase">Título curto</span>
                                <h4 className="font-black text-sm text-slate-800 dark:text-slate-100 mt-0.5">{sugestaoVisualizando.tituloCurto}</h4>
                            </div>

                            <div className="grid grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-100">
                                <div><span className="text-slate-400">Usuário:</span> <strong className="text-slate-700 dark:text-slate-200">{sugestaoVisualizando.usuario}</strong></div>
                                <div><span className="text-slate-400">Data:</span> <strong className="text-slate-700 dark:text-slate-200">{sugestaoVisualizando.data}</strong></div>
                                <div><span className="text-slate-400">Status:</span> <strong className="text-indigo-600">{sugestaoVisualizando.status}</strong></div>
                                <div><span className="text-slate-400">Prioridade:</span> <strong className="text-slate-700 dark:text-slate-200">{sugestaoVisualizando.prioridade}</strong></div>
                            </div>

                            <div>
                                <span className="text-[10px] font-black text-slate-400 uppercase">Descrição detalhada</span>
                                <p className="mt-1 p-3 bg-slate-50 dark:bg-slate-900 border rounded-xl text-slate-700 dark:text-slate-300 leading-relaxed">
                                    {sugestaoVisualizando.desc}
                                </p>
                            </div>

                            {sugestaoVisualizando.anexosLista && sugestaoVisualizando.anexosLista.length > 0 && (
                                <div>
                                    <span className="text-[10px] font-black text-slate-400 uppercase">Anexos salvos no Google Drive</span>
                                    <div className="mt-1 space-y-1.5">
                                        {sugestaoVisualizando.anexosLista.map((anx, i) => (
                                            <a key={i} href={anx.url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-2 rounded-lg border bg-blue-50/50 text-blue-700 hover:underline">
                                                <span>📁 {anx.nome}</span>
                                                <span className="text-[10px] text-slate-400">{anx.tamanho}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pt-3 flex justify-end border-t">
                                <button onClick={() => setSugestaoVisualizando(null)} className="px-5 py-2 rounded-xl bg-slate-800 text-white font-bold">Fechar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL HISTÓRICO DE AUDITORIA (🕒 HISTORY) */}
            {sugestaoHistorico && (
                <div className="facil-modal-overlay">
                    <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-fade-in space-y-0 text-xs">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                            <div className="flex items-center gap-2">
                                <History className="text-slate-600" size={18} />
                                <h3 className="font-black text-sm text-slate-800 dark:text-slate-100">Histórico de Alterações</h3>
                            </div>
                            <button onClick={() => setSugestaoHistorico(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
                        </div>

                        <div className="p-5 space-y-4">
                            <h4 className="font-bold text-slate-800 dark:text-slate-100">{sugestaoHistorico.tituloCurto}</h4>

                            <div className="space-y-3 pl-2 border-l-2 border-slate-200 dark:border-slate-700">
                                {(sugestaoHistorico.historico || []).map((h, i) => (
                                    <div key={i} className="relative pl-4 space-y-0.5">
                                        <div className="absolute -left-[17px] top-1 w-2.5 h-2.5 rounded-full bg-cyan-500"></div>
                                        <div className="font-bold text-slate-700 dark:text-slate-200">{h.acao}</div>
                                        <div className="text-[10px] text-slate-400">{h.data} • Por {h.autor}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="pt-3 flex justify-end border-t">
                                <button onClick={() => setSugestaoHistorico(null)} className="px-5 py-2 rounded-xl bg-slate-800 text-white font-bold">Fechar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL NOVA SUGESTÃO (PRINT 3 EXATAMENTE) */}
            {novaSugestaoModalAberta && (
                <div className="facil-modal-overlay">
                    <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl animate-fade-in space-y-0">
                        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-purple-100 dark:bg-purple-950 flex items-center justify-center text-purple-600">
                                    <MessageSquare size={18} />
                                </div>
                                <div>
                                    <h3 className="font-black text-sm text-slate-800 dark:text-slate-100">Nova sugestão</h3>
                                    <p className="text-xs text-slate-500">Registre sugestão, correção ou melhoria do sistema.</p>
                                </div>
                            </div>
                            <button onClick={() => setNovaSugestaoModalAberta(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSalvarNovaSugestao} className="p-6 space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-700 dark:text-slate-200">Título curto</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ex.: Ocultar botão da IA quando não houver pendências"
                                    value={formTitulo}
                                    onChange={(e) => setFormTitulo(e.target.value)}
                                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-700 dark:text-slate-200">Tipo</label>
                                    <select
                                        value={formTipo}
                                        onChange={(e) => setFormTipo(e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                                    >
                                        <option value="Sugestão">Sugestão</option>
                                        <option value="Correção">Correção</option>
                                        <option value="Melhoria">Melhoria</option>
                                    </select>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-700 dark:text-slate-200">Tela</label>
                                    <input
                                        type="text"
                                        value={formTela}
                                        onChange={(e) => setFormTela(e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-700 dark:text-slate-200">Descrição</label>
                                <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-900">
                                    <div className="flex items-center gap-1.5 p-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800">
                                        <button type="button" className="px-2 py-1 rounded bg-slate-100 text-slate-700 font-bold text-xs"><Bold size={12} /></button>
                                        <button type="button" className="px-2 py-1 rounded bg-slate-100 text-slate-700 font-bold text-xs"><Italic size={12} /></button>
                                        <button type="button" className="px-2 py-1 rounded bg-slate-100 text-slate-700 font-bold text-xs"><Underline size={12} /></button>
                                        <button type="button" className="px-2.5 py-1 rounded bg-slate-100 text-slate-700 font-bold text-xs">Lista</button>
                                        <button type="button" className="px-2.5 py-1 rounded bg-slate-100 text-slate-700 font-bold text-xs">1,2,3</button>
                                    </div>
                                    <textarea
                                        rows={4}
                                        required
                                        value={formDescricao}
                                        onChange={(e) => setFormDescricao(e.target.value)}
                                        className="w-full p-3 bg-transparent text-slate-800 dark:text-slate-100 text-xs focus:outline-none custom-scrollbar"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-black text-slate-700 dark:text-slate-200">Anexos (salvos na pasta do Google Drive)</label>
                                <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 flex items-center gap-3">
                                    <label className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 font-bold text-xs cursor-pointer hover:bg-slate-100">
                                        Escolher arquivos
                                        <input type="file" multiple className="hidden" onChange={(e) => setFormAnexos(Array.from(e.target.files))} />
                                    </label>
                                    <span className="text-xs text-slate-400">
                                        {formAnexos.length > 0 ? `${formAnexos.length} arquivo(s) selecionado(s)` : 'Nenhum arquivo escolhido'}
                                    </span>
                                </div>
                            </div>

                            <div className="pt-3 flex justify-end gap-2 border-t border-slate-100 dark:border-slate-800">
                                <button 
                                    type="button" 
                                    onClick={() => setNovaSugestaoModalAberta(false)}
                                    className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="px-5 py-2 rounded-xl text-xs font-black bg-[#4f46e5] hover:bg-[#4338ca] text-white shadow-sm"
                                >
                                    💾 Salvar sugestão
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL CONFIGURAÇÃO DA CENTRAL DE AJUDA */}
            {configModalAberto && (
                <div className="facil-modal-overlay">
                    <div className="bg-white dark:bg-[#08332e] border border-[#e7d8c3] dark:border-[#145c50] rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl animate-fade-in space-y-0">
                        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                            <div className="flex items-center gap-3">
                                <Settings className="text-amber-500" size={20} />
                                <div>
                                    <h3 className="font-black text-sm text-slate-800 dark:text-slate-100">Configuração da Central de Ajuda</h3>
                                    <p className="text-xs text-slate-500">Uso exclusivo do super administrador</p>
                                </div>
                            </div>
                            <button onClick={() => setConfigModalAberto(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSalvarConfigGeral} className="p-6 overflow-y-auto custom-scrollbar space-y-6 flex-1 text-xs">
                            
                            <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-2xl space-y-3 bg-slate-50/50 dark:bg-slate-900/50">
                                <h4 className="font-black text-xs text-slate-800 dark:text-slate-100 uppercase tracking-wider">Integração com Google Drive</h4>
                                
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <label className="font-bold text-slate-600 dark:text-slate-300">ID da pasta</label>
                                        <input
                                            type="text"
                                            value={configData.driveFolderId}
                                            onChange={(e) => setConfigData({ ...configData, driveFolderId: e.target.value })}
                                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="font-bold text-slate-600 dark:text-slate-300">URL publicada do Apps Script</label>
                                        <input
                                            type="text"
                                            value={configData.appsScriptUrl}
                                            onChange={(e) => setConfigData({ ...configData, appsScriptUrl: e.target.value })}
                                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="font-bold text-slate-600 dark:text-slate-300">Token de upload</label>
                                    <input
                                        type="password"
                                        value={configData.uploadToken}
                                        onChange={(e) => setConfigData({ ...configData, uploadToken: e.target.value })}
                                        className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                    />
                                </div>
                            </div>

                            <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-2xl space-y-3 bg-slate-50/50 dark:bg-slate-900/50">
                                <h4 className="font-black text-xs text-slate-800 dark:text-slate-100 uppercase tracking-wider">Manuais do sistema no Google Drive</h4>
                                
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <label className="font-bold text-slate-600 dark:text-slate-300">ID da pasta dos manuais</label>
                                        <input
                                            type="text"
                                            value={configData.manualDriveFolderId}
                                            onChange={(e) => setConfigData({ ...configData, manualDriveFolderId: e.target.value })}
                                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="font-bold text-slate-600 dark:text-slate-300">URL publicada do Apps Script do Manual</label>
                                        <input
                                            type="text"
                                            value={configData.manualAppsScriptUrl}
                                            onChange={(e) => setConfigData({ ...configData, manualAppsScriptUrl: e.target.value })}
                                            className="w-full p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                                <button type="button" onClick={() => setConfigModalAberto(false)} className="px-5 py-2.5 rounded-xl font-bold border border-slate-200 text-slate-700 bg-white">
                                    Fechar
                                </button>
                                <button type="submit" className="px-5 py-2.5 rounded-xl font-black bg-[#4f46e5] hover:bg-[#4338ca] text-white shadow-sm">
                                    💾 Salvar configurações
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            )}

            {/* DETALHES DO MANUAL SELECIONADO - THEME AWARE MATCHING PRINT 4 */}
            {selectedManual && (() => {
                const docId = extrairIdDocumentoGoogle(selectedManual);
                const isFav = favoritos.includes(selectedManual.id || selectedManual.documentId);
                const urlAbrir = docId ? `https://docs.google.com/document/d/${docId}/preview` : selectedManual.url;
                const urlIframe = docId ? `https://docs.google.com/document/d/${docId}/preview?embedded=true` : null;

                return (
                    <div className="facil-modal-overlay">
                        <div className="bg-[#fffdf8] dark:bg-[#08332e] border border-[#dac9b5] dark:border-[#145c50] rounded-2xl w-[96vw] max-w-6xl h-[92vh] flex flex-col overflow-hidden shadow-2xl animate-fade-in">
                            
                            {/* CABEÇALHO DO VISUALIZADOR DE MANUAL */}
                            <div className="p-4 border-b border-[#dac9b5] dark:border-[#145c50] flex items-center justify-between bg-[#fbf7f0] dark:bg-[#062522]">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900 flex items-center justify-center text-cyan-700 dark:text-cyan-300 shrink-0">
                                        <BookOpen size={16} />
                                    </div>
                                    <div className="min-w-0">
                                        <span className="text-[9px] font-black tracking-widest text-[#d7831f] uppercase">{selectedManual.modulo} • {selectedManual.menu || 'DOCUMENTOS'} • {selectedManual.area || 'CONSULTA'}</span>
                                        <h3 className="font-black text-sm text-slate-800 dark:text-slate-100 truncate">{selectedManual.titulo}</h3>
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Leitura aberta em modo visualização. Suas anotações ficam salvas apenas para o seu usuário neste navegador.</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    <button 
                                        onClick={() => toggleFavorito(selectedManual.id || selectedManual.documentId)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${isFav ? 'bg-amber-50 border-amber-300 text-amber-600' : 'bg-white dark:bg-slate-800 border-slate-200 text-slate-700'}`}
                                    >
                                        <Star size={13} className={isFav ? 'fill-current text-amber-500' : ''} />
                                        {isFav ? 'Favoritado' : 'Favorito'}
                                    </button>

                                    <button 
                                        onClick={() => setOcultarAnotacoes(v => !v)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                                    >
                                        <SidebarIcon size={13} />
                                        {ocultarAnotacoes ? 'Exibir coluna de anotações' : 'Ocultar coluna de anotações'}
                                    </button>

                                    {urlAbrir && (
                                        <a 
                                            href={urlAbrir}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                                        >
                                            <ExternalLink size={13} /> Abrir
                                        </a>
                                    )}

                                    <button 
                                        onClick={() => setSelectedManual(null)}
                                        className="w-8 h-8 rounded-full border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>
                            </div>

                            {/* CORPO DO VISUALIZADOR */}
                            <div className="flex-1 min-h-0 flex overflow-hidden">
                                
                                <div className="flex-1 min-w-0 bg-white dark:bg-slate-900 overflow-y-auto p-6 custom-scrollbar">
                                    {urlIframe ? (
                                        <iframe 
                                            title={selectedManual.titulo}
                                            src={urlIframe}
                                            className="w-full h-full border-0 min-h-[500px]"
                                        />
                                    ) : selectedManual.contentHtml ? (
                                        <div 
                                            dangerouslySetInnerHTML={{ __html: selectedManual.contentHtml }} 
                                            className="prose dark:prose-invert max-w-none"
                                        />
                                    ) : (
                                        <div className="space-y-4">
                                            <h2 className="text-xl font-black text-[#0f6f7a] dark:text-[#a7f3d0]">{selectedManual.titulo}</h2>
                                            <p className="text-xs text-slate-500">{selectedManual.resumo || 'Orientações detalhadas deste manual.'}</p>
                                            
                                            <div className="p-3 bg-[#e8f7f2] dark:bg-[#08332e] border border-[#9bd6c9] dark:border-[#145c50] rounded-xl text-xs font-bold text-[#0f6f7a] dark:text-[#a7f3d0]">
                                                ⚡ EM 1 MINUTO | Nesta aba você localiza documentos já gerados, confere empresa, período, responsável, status e arquivo, abre o documento para revisão e acompanha pendências.
                                            </div>

                                            <div className="p-3 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs font-bold text-emerald-800 dark:text-emerald-300">
                                                ✔️ RESULTADO ESPERADO | Ao final, o documento correto estará localizado, revisado e disponível para abertura, download ou reenvio.
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {!ocultarAnotacoes && (
                                    <div className="w-80 border-l border-[#dac9b5] dark:border-[#145c50] bg-[#fbf7f0] dark:bg-[#062522] p-4 flex flex-col justify-between space-y-4 shrink-0 overflow-y-auto">
                                        <div className="space-y-3">
                                            <div>
                                                <h4 className="font-black text-sm text-slate-800 dark:text-slate-100">Minhas anotações</h4>
                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                                                    Anotações locais e privadas deste manual. Elas não alteram o Google Docs original.
                                                </p>
                                            </div>

                                            <textarea
                                                rows={10}
                                                value={anotacaoTexto}
                                                onChange={(e) => handleSalvarAnotacao(e.target.value)}
                                                placeholder="Anotações gerais deste manual..."
                                                className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 custom-scrollbar"
                                            />
                                        </div>

                                        <div className="p-3 bg-white/60 dark:bg-slate-900/60 border border-slate-200/60 dark:border-slate-800 rounded-xl text-[10px] text-slate-400 leading-relaxed">
                                            As anotações desta lateral são particulares do seu usuário neste navegador.
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                );
            })()}

        </div>
    );
};

export default CentralAjuda;
